import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PasswordField from "../common/PasswordField";
import {
  getInitialSession,
  listenToAuthChanges,
  requestPasswordReset,
  signOutCurrentUser,
  updateCurrentUserPassword,
} from "../../services/supabase/authService";
import {
  isSupabaseConfigured,
  missingSupabaseConfig,
} from "../../services/supabase/config";

const getRecoveryIntentFromUrl = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(
    window.location.hash.replace(/^#/, "")
  );

  const type = searchParams.get("type") || hashParams.get("type");
  const hasCode = Boolean(searchParams.get("code"));
  const hasAccessToken = Boolean(
    hashParams.get("access_token") || searchParams.get("access_token")
  );

  return type === "recovery" || hasCode || hasAccessToken;
};

function RecuperarContrasena() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkingRecovery, setCheckingRecovery] = useState(true);
  const [recoveryIntent, setRecoveryIntent] = useState(false);
  const [recoveryReady, setRecoveryReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const missingConfigText = useMemo(
    () => missingSupabaseConfig.join(", "),
    []
  );
  const setupContext = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const invitedEmail = String(searchParams.get("email") || "").trim().toLowerCase();
    const invitedRole = String(searchParams.get("role") || "").trim().toLowerCase();

    return {
      enabled: searchParams.get("setup") === "1",
      email: invitedEmail,
      role: invitedRole,
    };
  }, [location.search]);
  const returnTo = useMemo(() => {
    const requested = new URLSearchParams(location.search).get("returnTo") || "";
    return requested.startsWith("/") && !requested.startsWith("//")
      ? requested
      : "/login";
  }, [location.search]);
  const setupRoleLabel = useMemo(() => {
    switch (setupContext.role) {
      case "mozo":
        return "mozo";
      case "cocina":
        return "cocina";
      case "admin_limitado":
      default:
        return setupContext.role ? "admin limitado" : "usuario invitado";
    }
  }, [setupContext.role]);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setCheckingRecovery(false);
      return undefined;
    }

    const nextRecoveryIntent = getRecoveryIntentFromUrl();
    setRecoveryIntent(nextRecoveryIntent);

    let isMounted = true;

    const syncRecoveryState = async () => {
      try {
        const session = await getInitialSession();

        if (!isMounted) {
          return;
        }

        if (nextRecoveryIntent && session) {
          setRecoveryReady(true);
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(error.message || "No se pudo validar el enlace de recuperacion.");
      } finally {
        if (isMounted) {
          setCheckingRecovery(false);
        }
      }
    };

    syncRecoveryState();

    const subscription = listenToAuthChanges((event, session) => {
      if (!isMounted) {
        return;
      }

      if (event === "PASSWORD_RECOVERY") {
        setRecoveryIntent(true);
        setRecoveryReady(true);
        setCheckingRecovery(false);
        return;
      }

      if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && nextRecoveryIntent && session) {
        setRecoveryReady(true);
        setCheckingRecovery(false);
      }
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe?.();
    };
  }, []);

  const isResetMode = recoveryIntent;
  const isInvitationSetupMode = setupContext.enabled;

  const handleRequestReset = async (event) => {
    event.preventDefault();

    setErrorMessage("");
    setSuccessMessage("");

    if (!email.trim()) {
      setErrorMessage("Ingresa el email de la cuenta que quieres recuperar.");
      return;
    }

    setSubmitting(true);

    try {
      await requestPasswordReset({
        email: email.trim(),
        redirectTo: `${window.location.origin}/recuperar-contrasena?returnTo=${encodeURIComponent(
          returnTo
        )}`,
      });

      setSuccessMessage(
        "Te enviamos un correo para recuperar la contrasena. Abre ese enlace y vuelve aqui para definir una nueva."
      );
      setEmail("");
    } catch (error) {
      setErrorMessage(error.message || "No se pudo enviar el correo de recuperacion.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePassword = async (event) => {
    event.preventDefault();

    setErrorMessage("");
    setSuccessMessage("");

    if (!password || !confirmPassword) {
      setErrorMessage("Completa ambas casillas para definir la nueva contrasena.");
      return;
    }

    if (password.length < 6) {
      setErrorMessage("La nueva contrasena debe tener al menos 6 caracteres.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Las contrasenas no coinciden.");
      return;
    }

    setSubmitting(true);

    try {
      await updateCurrentUserPassword({ password });
      if (isInvitationSetupMode) {
        setSuccessMessage(
          "Contrasena creada correctamente. Estamos entrando a tu acceso."
        );
        setPassword("");
        setConfirmPassword("");
        window.setTimeout(() => {
          navigate("/acceso", { replace: true });
        }, 200);
        return;
      }

      await signOutCurrentUser();
      setSuccessMessage(
        "Contrasena actualizada. Ya puedes volver al ingreso con tu nueva clave."
      );
      setPassword("");
      setConfirmPassword("");
      setRecoveryReady(false);
      setRecoveryIntent(false);
      window.setTimeout(() => {
        navigate(returnTo, { replace: true });
      }, 300);
    } catch (error) {
      setErrorMessage(error.message || "No se pudo actualizar la contrasena.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderRequestForm = () => (
    <form onSubmit={handleRequestReset} style={styles.form}>
      <label style={styles.label}>
        Email
        <input
          type="email"
          name="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          style={styles.input}
          placeholder="empresa@correo.com"
          disabled={submitting || !isSupabaseConfigured}
          autoComplete="email"
        />
      </label>

      <button
        type="submit"
        style={styles.primaryButton}
        disabled={submitting || !isSupabaseConfigured}
      >
        {submitting ? "Enviando correo..." : "Recuperar contrasena"}
      </button>
    </form>
  );

  const renderUpdateForm = () => (
    <form onSubmit={handleUpdatePassword} style={styles.form}>
      <PasswordField
        label="Nueva contrasena"
        name="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        placeholder="Minimo 6 caracteres"
        disabled={submitting || !recoveryReady}
        autoComplete="new-password"
      />

      <PasswordField
        label="Repetir nueva contrasena"
        name="confirmPassword"
        value={confirmPassword}
        onChange={(event) => setConfirmPassword(event.target.value)}
        placeholder="Repite la nueva contrasena"
        disabled={submitting || !recoveryReady}
        autoComplete="new-password"
      />

      <button
        type="submit"
        style={styles.primaryButton}
        disabled={submitting || !recoveryReady}
      >
        {submitting ? "Guardando nueva contrasena..." : "Actualizar contrasena"}
      </button>
    </form>
  );

  return (
    <div style={styles.page}>
      <div style={styles.panel}>
        <div style={styles.brand}>MozzoApp</div>
        <h1 style={styles.title}>
          {isResetMode
            ? isInvitationSetupMode
              ? "Crea tu contrasena"
              : "Define una nueva contrasena"
            : "Recupera tu acceso"}
        </h1>
        <p style={styles.subtitle}>
          {isResetMode
            ? isInvitationSetupMode
              ? "Abre el enlace del correo y define una contrasena para terminar el alta del acceso invitado."
              : "Cuando abras el enlace del correo, podras cargar una contrasena nueva para volver a entrar."
            : "Ingresa el email de tu cuenta y te enviaremos un enlace para restablecer la contrasena."}
        </p>

        {isInvitationSetupMode && setupContext.email ? (
          <div style={{ ...styles.banner, ...styles.bannerInfo }}>
            Este correo fue invitado como <strong>{setupRoleLabel}</strong>:{" "}
            <strong>{setupContext.email}</strong>. Cuando guardes la contrasena, quedara listo
            para entrar a la app con ese acceso.
          </div>
        ) : null}

        {!isSupabaseConfigured && (
          <div style={{ ...styles.banner, ...styles.bannerWarning }}>
            Falta configurar Supabase en el frontend: {missingConfigText}
          </div>
        )}

        {checkingRecovery && isResetMode && (
          <div style={{ ...styles.banner, ...styles.bannerInfo }}>
            Estamos validando el enlace de recuperacion para habilitar el cambio de contrasena.
          </div>
        )}

        {errorMessage && (
          <div style={{ ...styles.banner, ...styles.bannerError }}>
            {errorMessage}
          </div>
        )}

        {successMessage && (
          <div style={{ ...styles.banner, ...styles.bannerSuccess }}>
            {successMessage}
          </div>
        )}

        {isResetMode ? renderUpdateForm() : renderRequestForm()}

        <div style={styles.footerRow}>
          <Link to={returnTo} style={styles.secondaryLink}>
            Volver al ingreso
          </Link>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    background:
      "linear-gradient(145deg, rgba(241,245,249,1) 0%, rgba(248,250,252,1) 55%, rgba(226,232,240,1) 100%)",
  },
  panel: {
    width: "100%",
    maxWidth: "520px",
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    padding: "32px",
    boxShadow: "0 24px 48px rgba(15, 23, 42, 0.12)",
    border: "1px solid rgba(148, 163, 184, 0.2)",
  },
  brand: {
    display: "inline-flex",
    padding: "8px 14px",
    borderRadius: "999px",
    backgroundColor: "#eef2ff",
    color: "#4338ca",
    fontWeight: 700,
    marginBottom: "18px",
  },
  title: {
    marginTop: 0,
    marginBottom: "8px",
    color: "#0f172a",
  },
  subtitle: {
    marginTop: 0,
    marginBottom: "24px",
    color: "#475569",
    lineHeight: 1.5,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  label: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    color: "#1e293b",
    fontWeight: 600,
  },
  input: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "12px 14px",
    fontSize: "15px",
    boxSizing: "border-box",
  },
  primaryButton: {
    marginTop: "8px",
    width: "100%",
    border: "none",
    borderRadius: "14px",
    padding: "14px 16px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    fontWeight: 700,
    cursor: "pointer",
  },
  footerRow: {
    marginTop: "18px",
    display: "flex",
    justifyContent: "flex-start",
  },
  secondaryLink: {
    color: "#2563eb",
    fontWeight: 600,
    textDecoration: "none",
  },
  banner: {
    borderRadius: "12px",
    padding: "12px 14px",
    fontSize: "14px",
    lineHeight: 1.45,
    marginBottom: "16px",
  },
  bannerWarning: {
    backgroundColor: "#fff7ed",
    border: "1px solid #fdba74",
    color: "#9a3412",
  },
  bannerError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fca5a5",
    color: "#b91c1c",
  },
  bannerSuccess: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  bannerInfo: {
    backgroundColor: "#eff6ff",
    border: "1px solid #93c5fd",
    color: "#1d4ed8",
  },
};

export default RecuperarContrasena;
